/**
 * Settings page rows of the export preferences (W2-09 publish-import; DESIGN.md §4.20): custom editors, because the
 * preferences have defaults the generic editors cannot know (an unset `ext` value means the default here).
 */

import { useCallback, useSyncExternalStore, type ComponentType } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import { Select } from '../../ui/Select';
import { Switch } from '../../ui/Switch';
import { useText } from './hooks';
import { readPrefs, writePref, type ExportPrefs } from './prefs';

type BoolPref = { [K in keyof ExportPrefs]: ExportPrefs[K] extends boolean ? K : never }[keyof ExportPrefs];
type ChoicePref = 'destination' | 'floatStyle';

function usePref<K extends keyof ExportPrefs>(services: AppServices, key: K): ExportPrefs[K] {
  const subscribe = useCallback(
    (cb: () => void) =>
      services.settings.subscribe(() => {
        cb();
      }),
    [services],
  );
  const read = useCallback(() => readPrefs(services.settings)[key], [services, key]);
  return useSyncExternalStore(subscribe, read, read);
}

/** A switch row for a boolean preference. */
export function prefSwitch(key: BoolPref, title: MessageKey): ComponentType<{ readonly services: AppServices }> {
  function PrefSwitch(props: { readonly services: AppServices }) {
    const { services } = props;
    const text = useText(services);
    const value = usePref(services, key);
    return (
      <Switch
        label={<span className="visually-hidden">{text.t(title)}</span>}
        checked={value}
        onChange={(v) => {
          writePref(services.settings, key, v);
        }}
      />
    );
  }
  PrefSwitch.displayName = `PrefSwitch(${key})`;
  return PrefSwitch;
}

/** A select row for a preference with fixed choices. */
export function prefSelect<K extends ChoicePref>(
  key: K,
  title: MessageKey,
  options: ReadonlyArray<{ readonly value: ExportPrefs[K]; readonly label: MessageKey }>,
): ComponentType<{ readonly services: AppServices }> {
  function PrefSelect(props: { readonly services: AppServices }) {
    const { services } = props;
    const text = useText(services);
    const value = usePref(services, key);
    return (
      <Select<ExportPrefs[K]>
        ariaLabel={text.t(title)}
        value={value}
        options={options.map((o) => ({ value: o.value, label: text.t(o.label) }))}
        onChange={(v) => {
          writePref(services.settings, key, v);
        }}
      />
    );
  }
  PrefSelect.displayName = `PrefSelect(${key})`;
  return PrefSelect;
}
